import React from "react";

import {
  FormattedMessage,
  FormattedNumber,
  FormattedPlural,
  injectIntl,
  IntlShape,
} from "react-intl";

import { Text } from "@/components/atoms/Text";
import TimeIcon from "@/icons/__generated__/Time/Time16";
import { cn } from "@/lib/utils";
import { IHostResponsiveness } from "~/types/api-types";

interface HostResponsivenessProps {
  intl: IntlShape;
  hostResponsiveness?: IHostResponsiveness;
  ownerName?: string;
  isFullServiceVenue?: boolean;
}

const HostResponsiveness: React.FC<HostResponsivenessProps> = ({
  intl,
  ownerName,
  hostResponsiveness,
  isFullServiceVenue,
}) => {
  const getResponseTimeMessage = (hours: number) => {
    if (hours === 1) {
      return <FormattedMessage defaultMessage="within 1 hr" />;
    }

    if (hours > 48) {
      return (
        <FormattedMessage
          defaultMessage="in over {hours} hrs"
          values={{ hours: <FormattedNumber value={hours} /> }}
        />
      );
    }

    let displayedHours: number;
    if (hours < 9) {
      displayedHours = hours;
    } else if (hours < 13) {
      displayedHours = 12;
    } else if (hours < 25) {
      displayedHours = 24;
    } else {
      displayedHours = 48;
    }

    return (
      <FormattedMessage
        defaultMessage="within {hours} hrs"
        values={{
          hours: <FormattedNumber value={displayedHours} />,
        }}
      />
    );
  };

  const getHostName = () => {
    if (isFullServiceVenue) {
      return intl.formatMessage({ defaultMessage: "Venue" });
    }

    return ownerName || intl.formatMessage({ defaultMessage: "You" });
  };

  const responseLevel = hostResponsiveness?.level ?? 0;
  const responseTime = hostResponsiveness?.hours ?? 0;

  return (
    <Text
      typeface="body-1-m-long"
      className="tw-text-center tw-flex tw-items-center tw-gap-8"
    >
      <TimeIcon
        className={cn("tw-text-green-800", {
          "tw-text-yellow-600": responseLevel === 2,
          "tw-text-red-800": responseLevel === 3,
        })}
      />
      <span>
        <span className="tw-inline-block">
          <FormattedMessage
            defaultMessage="{hostName} typically {responds}"
            values={{
              hostName: getHostName(),
              responds: (
                <FormattedPlural
                  value={ownerName ? 2 : 1}
                  one={intl.formatMessage({
                    defaultMessage: "respond",
                  })}
                  other={intl.formatMessage({
                    defaultMessage: "responds",
                  })}
                />
              ),
            }}
          />
        </span>
        &nbsp;
        <span className="tw-inline-block">
          {getResponseTimeMessage(responseTime)}
        </span>
      </span>
    </Text>
  );
};

export default injectIntl(HostResponsiveness);
